import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { supabase } from "@/lib/supabase";
import type { Role } from "@/lib/roles";

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: Role;
  department: string;
  status: "active" | "locked";
  initials: string;
}

interface LoginResult {
  success: boolean;
  error?: string;
}

interface AdminAuthContextValue {
  user: AdminUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  logout: () => void;
  resetPassword: (email: string) => Promise<LoginResult>;
  updatePassword: (newPassword: string) => Promise<LoginResult>;
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

function initialsFrom(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0])
      .join("")
      .toUpperCase() || "U"
  );
}

interface ProfileRow {
  id: string;
  email: string;
  name: string;
  role: Role;
  department?: string | null;
  status?: string | null;
}

function toAdminUser(p: ProfileRow): AdminUser {
  return {
    id: p.id,
    name: p.name ?? "Administrator",
    email: p.email ?? "",
    role: p.role,
    department: p.department ?? "",
    status: p.status === "locked" ? "locked" : "active",
    initials: initialsFrom(p.name ?? "Administrator"),
  };
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async (authId: string) => {
    const { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", authId)
      .maybeSingle();

    if (data) {
      setUser(toAdminUser(data as ProfileRow));
    } else {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    let active = true;

    // Bootstrap the primary Super Admin if it doesn't exist yet (idempotent).
    supabase.functions.invoke("bootstrap-admin").catch(() => {
      /* best-effort seed; login still works once a super admin exists */
    });

    supabase.auth.getSession().then(({ data }) => {
      if (data.session?.user) {
        void loadProfile(data.session.user.id);
      }
      if (active) setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        void loadProfile(session.user.id);
      } else {
        setUser(null);
      }
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, [loadProfile]);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      const normalized = email.trim().toLowerCase();
      const { data, error } = await supabase.auth.signInWithPassword({
        email: normalized,
        password,
      });

      if (error) {
        const msg =
          error.message.toLowerCase().includes("invalid login") ||
          error.message.toLowerCase().includes("invalid email")
            ? "Incorrect email or password."
            : error.message;
        return { success: false, error: msg };
      }

      if (data.user) {
        await loadProfile(data.user.id);
      }

      return { success: true };
    },
    [loadProfile],
  );

  const logout = useCallback(() => {
    void supabase.auth.signOut();
    setUser(null);
  }, []);

  // Sends a password reset email (Supabase "Forgot password" recovery flow).
  const resetPassword = useCallback(
    async (email: string): Promise<LoginResult> => {
      const normalized = email.trim().toLowerCase();
      if (!normalized || !normalized.includes("@")) {
        return { success: false, error: "Please enter a valid email address." };
      }
      const redirectTo = `${window.location.origin}${__BASE_PATH__}/admin/reset-password`;
      const { error } = await supabase.auth.resetPasswordForEmail(normalized, {
        redirectTo,
      });
      if (error) {
        return { success: false, error: error.message };
      }
      return { success: true };
    },
    [],
  );

  // Updates the signed-in user's password after a recovery link is used.
  const updatePassword = useCallback(
    async (newPassword: string): Promise<LoginResult> => {
      if (newPassword.length < 8) {
        return { success: false, error: "Password must be at least 8 characters." };
      }
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) {
        return { success: false, error: error.message };
      }
      return { success: true };
    },
    [],
  );

  return (
    <AdminAuthContext.Provider
      value={{ user, loading, login, logout, resetPassword, updatePassword }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth(): AdminAuthContextValue {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) {
    throw new Error("useAdminAuth must be used within an AdminAuthProvider");
  }
  return ctx;
}